/**
 * @module Component
 */

/**
 * Component object. A Component holds functionality and optional data.
 * It is held by a `ComponentManager`, which calls update methods appropriately.
 * @class Component
 * @param {Object} [config] - composite parameter object
 * @param {Boolean} [config.active=true] - whether the manager should run this
 * @param {String} [config.name="Untitled Component"] - component name
 * @param {Object} [config.owner] - entity to own the Component
 * @param {Function} [config.preUpdate] - method to run in pre-update phase
 * @param {Function} [config.update] - method to run in update phase
 * @param {Function} [config.postUpdate] - method to run in post-update phase
 */

const Component = class {

    constructor( config ) {
        config = config || {};

        /**
         * Whether the `ComponentManager` should update this component
         * @default true
         */
        this.active = config.active === false ? false : true;

        /**
         * Name of this component. It's good to give it a unique name,
         * so it can be accessed within the `ComponentManager`.
         * @type string
         * @default "Untitled Component"
         */
        this.name = config.name || "Untitled Component";

        /**
         * Owner of this component. This will be added by the
         * `ComponentManager` when the `Component` is assigned.
         */
        this.owner = config.owner;

        if ( config.preUpdate ) {
            this.preUpdate = config.preUpdate;
        }
        if ( config.update ) {
            this.update = config.update;
        }
        if ( config.postUpdate ) {
            this.postUpdate = config.postUpdate;
        }
    }

    /**
     * Run a pre-update on this Component.
     * 
     * Override this method. This is the workhorse of components.
     * 
     * @param {Number} time Game time
     * @param {Number} delta Frame duration
     */
    preUpdate( time, delta ) {}

    /**
     * Run an update on this Component.
     * 
     * Override this method. This is the workhorse of components.
     * 
     * @param {Number} time Game time
     * @param {Number} delta Frame duration
     */
    update( time, delta ) {}

    /**
     * Run a post-update on this Component.
     * 
     * Override this method. This is the workhorse of components.
     * 
     * @param {Number} time Game time
     * @param {Number} delta Frame duration
     */
    postUpdate( time, delta ) {}
};

export default Component;
